import type { CampoEditable, CampoEstilo } from "./editableFields";
import type { FaqItemDTO, TiendaContenidoDTO } from "./types";

export const INT_VERSION_PROTOCOLO_EDITOR = 1;

interface MensajeEditorBase {
    intVersionProtocolo: number;
    tipo: string;
}

export interface ActualizarCampoMensaje extends MensajeEditorBase {
    tipo: "ACTUALIZAR_CAMPO";
    uniIdBloque?: string;
    vchTipo: string;
    intOrden: number;
    campo: string;
    valor: string;
}

export interface ActualizarEstiloMensaje extends MensajeEditorBase {
    tipo: "ACTUALIZAR_ESTILO";
    uniIdBloque?: string;
    vchTipo: string;
    intOrden: number;
    campo: string;
    valor: string;
}

export interface ActualizarFaqsMensaje extends MensajeEditorBase {
    tipo: "ACTUALIZAR_FAQS";
    faqs: FaqItemDTO[];
}

export interface SolicitarContenidoMensaje extends MensajeEditorBase {
    tipo: "SOLICITAR_CONTENIDO";
    vchSolicitudId?: string;
}

export type MensajeAdministradorATienda =
    | ActualizarCampoMensaje
    | ActualizarEstiloMensaje
    | ActualizarFaqsMensaje
    | SolicitarContenidoMensaje;

export interface EditorListoMensaje extends MensajeEditorBase {
    tipo: "EDITOR_LISTO";
    bitContenidoMigrado: boolean;
}

export interface BloqueSeleccionadoMensaje extends MensajeEditorBase {
    tipo: "BLOQUE_SELECCIONADO";
    uniIdBloque: string;
    vchTipo: string;
    intOrden: number;
    campos: CampoEditable[];
    valores: Record<string, string>;
    camposEstilo: CampoEstilo[];
    valoresEstilo: Record<string, string>;
}

export interface BloqueDeseleccionadoMensaje extends MensajeEditorBase {
    tipo: "BLOQUE_DESELECCIONADO";
}

export interface ContenidoActualMensaje extends MensajeEditorBase {
    tipo: "CONTENIDO_ACTUAL";
    contenido: TiendaContenidoDTO;
    vchSolicitudId?: string;
}

export type MensajeTiendaAAdministrador =
    | EditorListoMensaje
    | BloqueSeleccionadoMensaje
    | BloqueDeseleccionadoMensaje
    | ContenidoActualMensaje;

const esRegistro = (valor: unknown): valor is Record<string, unknown> =>
    typeof valor === "object" && valor !== null && !Array.isArray(valor);

const esVersionCompatible = (valor: Record<string, unknown>): boolean =>
    valor.intVersionProtocolo === undefined ||
    (typeof valor.intVersionProtocolo === "number" &&
        Number.isInteger(valor.intVersionProtocolo) &&
        valor.intVersionProtocolo >= 1 &&
        valor.intVersionProtocolo <= INT_VERSION_PROTOCOLO_EDITOR);

const esFaqValida = (valor: unknown): valor is FaqItemDTO =>
    esRegistro(valor) &&
    typeof valor.vchPregunta === "string" &&
    typeof valor.vchRespuesta === "string";

export const esMensajeAdministradorATienda = (
    valor: unknown,
): valor is MensajeAdministradorATienda => {
    if (!esRegistro(valor) || typeof valor.tipo !== "string" || !esVersionCompatible(valor)) {
        return false;
    }

    if (valor.tipo === "SOLICITAR_CONTENIDO") {
        return valor.vchSolicitudId === undefined || typeof valor.vchSolicitudId === "string";
    }
    if (valor.tipo === "ACTUALIZAR_FAQS") {
        return Array.isArray(valor.faqs) && valor.faqs.every(esFaqValida);
    }
    if (valor.tipo !== "ACTUALIZAR_CAMPO" && valor.tipo !== "ACTUALIZAR_ESTILO") {
        return false;
    }

    return (
        typeof valor.vchTipo === "string" &&
        valor.vchTipo.trim().length > 0 &&
        typeof valor.intOrden === "number" &&
        Number.isInteger(valor.intOrden) &&
        typeof valor.campo === "string" &&
        valor.campo.trim().length > 0 &&
        typeof valor.valor === "string" &&
        (valor.uniIdBloque === undefined || typeof valor.uniIdBloque === "string")
    );
};

export const crearMensajeEditor = <TMensaje extends MensajeEditorBase>(
    mensaje: Omit<TMensaje, "intVersionProtocolo">,
): TMensaje =>
    ({
        ...mensaje,
        intVersionProtocolo: INT_VERSION_PROTOCOLO_EDITOR,
    }) as TMensaje;

export const enviarMensajeAlAdministrador = (
    mensaje: MensajeTiendaAAdministrador,
    vchOrigenPadre: string | null,
): void => {
    window.parent.postMessage(mensaje, vchOrigenPadre ?? "*");
};
